import React, { FC, useState } from 'react';
import {
    Button,
    DialogActions,
    Grid,
    TextField,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Theme,
    Paper,
    FormHelperText,
    DialogContent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AuthMethod, Role, Technician, User } from 'models/user';
import AuthMethodFields, {
    authMethodVariables,
    validateAuthMethodFields,
} from 'components/users/shared/auth-method-fields/auth-method-fields.component';
import { useFormik } from 'formik';
import { Organization } from 'models/organization';
import { ApolloCache, Reference, useMutation, useQuery } from '@apollo/client';
import { getRolesTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { addTechnicianTemplate } from 'apollo/templates/user/mutation.graphql';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { nameRegExp } from 'config/regex';

type AddTechnicianFormProps = {
    email: string;
    onDialogCloseClick: () => void;
    onAddTechnicianClick: (user: User) => void;
    isIsolatedUsersEnable: boolean;
};

const AddTechnicianForm: FC<AddTechnicianFormProps> = ({
    onDialogCloseClick,
    onAddTechnicianClick,
    email,
    isIsolatedUsersEnable,
}: AddTechnicianFormProps) => {
    const { t: translate } = useTranslation(['common', 'users']);
    const currentUser = currentUserVar();
    const [isFirstNameValid, setIsFirstNameValid] = useState<boolean>(false);
    const [isLastNameValid, setIsLastNameValid] = useState<boolean>(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { roles } = {}, loading: isRolesLoading } = useQuery(getRolesTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: { type: 'TECHNICIAN' },
        onError(error) {
            showBoundary(error);
        },
    });

    const [addTechnician] = useMutation(addTechnicianTemplate, {
        onCompleted: ({ addTechnician }) => {
            onAddTechnicianClick(addTechnician);
            onDialogCloseClick();
        },
        onError: (err) => {
            showBoundary(err);
        },
        update(cache: ApolloCache<User>, { data: { addTechnician } }) {
            cache.modify({
                fields: {
                    technicians(technicianRefs: Reference | (Reference | undefined)[], { toReference }) {
                        if (technicianRefs instanceof Array) {
                            return [...technicianRefs, toReference(addTechnician)];
                        }
                        return [technicianRefs, toReference(addTechnician)];
                    },
                },
            });
        },
    });

    const validate = (values: Technician) => {
        const errors: Record<string, string> = {};
        if (!values.firstName) {
            errors.firstName = translate('users:VALIDATION:FIRST_NAME');
        }
        if (values.firstName && !isFirstNameValid) {
            errors.firstName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if (!values.lastName) {
            errors.lastName = translate('users:VALIDATION:LAST_NAME');
        }
        if (values.lastName && !isLastNameValid) {
            errors.lastName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if (!values.email) {
            errors.email = translate('users:VALIDATION:EMAIL');
        }
        if (!values.role) {
            errors.role = translate('users:VALIDATION:ROLE');
        }
        if (!values.organization) {
            errors.organization = translate('users:VALIDATION:LAB');
        }
        validateAuthMethodFields(values, translate, errors);

        return errors;
    };

    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            role: '',
            email: email,
            organization: {
                _id: (currentUser.organization as Organization)._id,
                name: (currentUser.organization as Organization).name,
            } as Organization,
            active: true,
            isShared: false,
            authMethod: AuthMethod.PASSWORD,
            syncid: '',
            enableUserSync: false,
            personalIdentityNumber: '',
        },
        validateOnMount: true,
        validate,
        onSubmit: async (values: Technician, { resetForm }) => {
            await addTechnician({
                variables: {
                    name: {
                        firstName: values.firstName,
                        lastName: values.lastName,
                    },
                    email: values.email,
                    role: values.role,
                    isShared: values.isShared,
                    organization: (values.organization as Organization)?._id,
                    ...authMethodVariables(values),
                },
            });
            resetForm();
        },
    });

    const handleFirstNameChange = (newFirstName: string) => {
        const firstName = newFirstName.charAt(0).toUpperCase() + newFirstName.slice(1);
        setIsFirstNameValid(nameRegExp.test(firstName));
        formik.setFieldValue('firstName', firstName);
    };

    const handleLastNameChange = (newLastName: string) => {
        const lastName = newLastName.charAt(0).toUpperCase() + newLastName.slice(1);
        setIsLastNameValid(nameRegExp.test(lastName));
        formik.setFieldValue('lastName', lastName);
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <DialogContent>
                <Paper sx={{ padding: 2 }} variant="outlined">
                    <Grid container spacing={1}>
                        <Grid item xs={4}>
                            <FormControl fullWidth margin="dense">
                                <TextField
                                    id="firstName"
                                    label={translate('common:FIRST_NAME')}
                                    helperText={formik.touched.firstName && formik.errors.firstName}
                                    error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                                    onChange={(e) => handleFirstNameChange(e.target.value)}
                                    onBlur={formik.getFieldProps('firstName').onBlur}
                                    name={formik.getFieldProps('firstName').name}
                                    value={formik.getFieldProps('firstName').value}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                            <FormControl fullWidth margin="dense">
                                <TextField
                                    id="lastName"
                                    label={translate('common:LAST_NAME')}
                                    helperText={formik.touched.lastName && formik.errors.lastName}
                                    error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                                    onChange={(e) => handleLastNameChange(e.target.value)}
                                    onBlur={formik.getFieldProps('lastName').onBlur}
                                    name={formik.getFieldProps('lastName').name}
                                    value={formik.getFieldProps('lastName').value}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                            <FormControl fullWidth margin="dense">
                                <TextField
                                    id="email"
                                    label={translate('common:EMAIL')}
                                    type="email"
                                    disabled={!isIsolatedUsersEnable}
                                    helperText={formik.touched.email && formik.errors.email}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    {...formik.getFieldProps('email')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                            {!isRolesLoading && (
                                <FormControl
                                    margin="dense"
                                    fullWidth
                                    error={formik.touched.role && Boolean(formik.errors.role)}
                                >
                                    <InputLabel id="select-role">{translate('common:ROLE')}</InputLabel>
                                    <Select
                                        id="select-role"
                                        label={translate('common:ROLE')}
                                        error={formik.touched.role && Boolean(formik.errors.role)}
                                        {...formik.getFieldProps('role')}
                                    >
                                        {roles.map((role: Role) => (
                                            <MenuItem key={role.role} value={role.role}>
                                                {translate(role.role)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.role && formik.errors.role ? (
                                        <FormHelperText>{formik.errors.role}</FormHelperText>
                                    ) : undefined}
                                </FormControl>
                            )}
                        </Grid>
                        <AuthMethodFields formik={formik} />
                    </Grid>
                </Paper>
            </DialogContent>
            <DialogActions>
                <Button onClick={onDialogCloseClick}>{translate('common:CANCEL')}</Button>
                <Button disabled={!formik.isValid} variant="contained" type="submit">
                    {translate('common:SAVE')}
                </Button>
            </DialogActions>
        </form>
    );
};

export default AddTechnicianForm;
